import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ExpiryWarning = ({ user, className = "" }) => {
  const [expiryInfo, setExpiryInfo] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkExpiryWarning = () => {
      if (!user) {
        return;
      }

      // If user has used trial but no subscription_valid_until, show warning
      if (user.has_used_trial && !user.subscription_valid_until) {
        setExpiryInfo({
          daysUntilExpiry: -1, // Negative to indicate expired/no subscription
          expiryDate: 'No active subscription',
          planName: user.subscription_plan || 'No plan'
        });
        setIsVisible(true);
        return;
      }

      // If user has subscription_valid_until, check expiry
      if (user.subscription_valid_until) {
        const expiryDate = new Date(user.subscription_valid_until);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

        // Show warning if subscription expires in 7 days or less, or is already expired
        if (daysUntilExpiry <= 7) {
          setExpiryInfo({
            daysUntilExpiry,
            expiryDate: expiryDate.toLocaleDateString(),
            planName: user.subscription_plan
          });
          setIsVisible(true);
        }
      }
    };

    checkExpiryWarning();
  }, [user]);

  const handleRenew = () => {
    console.log('🔄 Navigating to pricing page...');
    try {
      navigate('/pricing');
      console.log('✅ Navigation triggered successfully');
    } catch (error) {
      console.error('❌ Navigation error:', error);
      // Fallback: use window.location
      window.location.href = '/pricing';
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible || !expiryInfo) {
    return null;
  }

  const getWarningColor = () => {
    if (expiryInfo.daysUntilExpiry === -1 || expiryInfo.daysUntilExpiry <= 2) {
      return 'bg-red-50 border-red-200 text-red-800';
    } else if (expiryInfo.daysUntilExpiry <= 4) {
      return 'bg-orange-50 border-orange-200 text-orange-800';
    } else {
      return 'bg-yellow-50 border-yellow-200 text-yellow-800';
    }
  };

  const getWarningIcon = () => {
    if (expiryInfo.daysUntilExpiry === -1 || expiryInfo.daysUntilExpiry <= 2) {
      return '🚨';
    } else if (expiryInfo.daysUntilExpiry <= 4) {
      return '⚠️';
    } else {
      return '⏰';
    }
  };

  return (
    <div className={`${getWarningColor()} border-l-4 p-4 rounded-md mb-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-xl mr-3">{getWarningIcon()}</span>
          <div>
            <h3 className="font-semibold">
              {expiryInfo.daysUntilExpiry === -1
                ? 'Your trial has ended!'
                : expiryInfo.daysUntilExpiry <= 0 
                ? 'Your subscription has expired!' 
                : expiryInfo.daysUntilExpiry === 1 
                ? 'Your subscription expires tomorrow!' 
                : `Your subscription expires in ${expiryInfo.daysUntilExpiry} days!`}
            </h3>
            <p className="text-sm mt-1">
              {expiryInfo.daysUntilExpiry === -1
                ? `Your free trial has ended. Choose a plan to continue enjoying all features.`
                : expiryInfo.daysUntilExpiry <= 0 
                ? `Your ${expiryInfo.planName} plan expired on ${expiryInfo.expiryDate}. Renew now to restore access to all features.`
                : `Your ${expiryInfo.planName} plan expires on ${expiryInfo.expiryDate}. Renew now to continue enjoying all features.`}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleRenew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
          >
            {expiryInfo.daysUntilExpiry === -1 ? 'Choose Plan' : 
             expiryInfo.daysUntilExpiry <= 0 ? 'Restore Access' : 'Renew Plan'}
          </button>
          <button
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-700 px-2 py-1 text-sm"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpiryWarning;
